import styled from "styled-components";

import {
  Modal as ModalAnt,
  Table as TableAnt,
  Dropdown as DropdownAnt,
  Menu as MenuAnt,
} from "antd";

import { DotsHorizontal, InformationCircle } from "../../styles/Icons";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";

interface StatusBadgeProps {
  statusType: "success" | "error" | "warning" | "default";
  width?: string;
}

interface FieldBadgeProps {
  fieldType: "setup" | "undefined";
  width?: string;
}

interface TableProps {
  scrollHeight?: number;
}

export const Table = styled(TableAnt)<TableProps>`
  width: 100%;

  .ant-table-tbody > tr > td {
    padding: 12px 8px;
    height: 60px;
    vertical-align: middle;
  }

  .ant-table-thead > tr > th {
    padding: 12px 8px;
    white-space: nowrap;
    vertical-align: middle;
  }

  .ant-table-cell {
    overflow: hidden;
  }

  .title-table {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .company-name,
  .city-name,
  .cnpj-ruc {
    text-transform: capitalize;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .cnpj-ruc {
    text-transform: none;
  }
  .certificate {
    display: flex;
    flex-direction: column;
    min-height: 42px;
    justify-content: center;
  }

  .expiration_date {
    font-size: 0.7rem;
    color: var(--gray-550);
    font-style: italic;
    margin-top: 4px;
    display: block;
    line-height: 1.2;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .field-normal {
    color: var(--gray-550);
    font-weight: normal;
  }
`;

const getStatusColors = (statusType: StatusBadgeProps["statusType"]) => {
  switch (statusType) {
    case "success":
      return {
        color: "var(--green-700)",
        backgroundColor: "#f6ffed",
        borderColor: "#b7eb8f",
      };
    case "error":
      return {
        color: "var(--red-600)",
        backgroundColor: "#fff2f0",
        borderColor: "#ffccc7",
      };
    case "warning":
      return {
        color: "#fa8c16",
        backgroundColor: "#fff7e6",
        borderColor: "#ffd591",
      };
    case "default":
    default:
      return {
        color: "var(--gray-550)",
        backgroundColor: "#fafafa",
        borderColor: "#d9d9d9",
      };
  }
};

export const TitleTable = styled.span`
  display: block;
  max-width: auto;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const StatusBadge = styled.span<StatusBadgeProps>`
  ${({ statusType }) => {
    const colors = getStatusColors(statusType);
    return `
      color: ${colors.color};
      background-color: ${colors.backgroundColor};
      border: 1px solid ${colors.borderColor};
    `;
  }}
  padding: 1px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: ${({ width }) => width || "auto"};
  display: inline-block;
  height: 24px;
  line-height: 22px;
`;

const getFieldColors = (fieldType: FieldBadgeProps["fieldType"]) => {
  switch (fieldType) {
    case "setup":
      return {
        color: "#fa8c16",
        backgroundColor: "#fff7e6",
        borderColor: "#ffd591",
      };
    case "undefined":
      return {
        color: "#722ed1",
        backgroundColor: "#f9f0ff",
        borderColor: "#d3adf7",
      };
  }
};

export const FieldBadge = styled.span<FieldBadgeProps>`
  ${({ fieldType }) => {
    const colors = getFieldColors(fieldType);
    return `
      color: ${colors.color};
      background-color: ${colors.backgroundColor};
      border: 1px solid ${colors.borderColor};
    `;
  }}
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: inline-block;

  height: 24px;
  line-height: 20px;
  text-align: center;

  .ant-table-thead > tr > th > span {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .in-setup {
    color: #fa8c16;
    background-color: #fff7e6;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ffd591;
    font-weight: 500;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .undefined-status {
    color: #722ed1;
    background-color: #f9f0ff;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #d3adf7;
    font-weight: 500;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const MoreIcon = styled(DotsHorizontal)`
  width: 1.5rem;
  height: 1.5rem;
  color: var(--orange-100);
  cursor: pointer;
`;

export const InformationCirclIcon = styled(InformationCircle)`
  width: 1.1rem;
  height: 1.1rem;
  color: var(--orange-100);
  cursor: pointer;
`;

export const Dropdown = styled(DropdownAnt)``;

export const Modal = styled(ModalAnt)``;

export const Menu = styled(MenuAnt)``;

export const ModalCompanyUsers = styled(ModalAnt)`
  width: 44rem;
  height: 53rem;
  .ant-modal-header {
    padding: 3rem 2rem 1rem;
  }
  .ant-modal-title {
    color: var(--gray-530);
    font-size: 2rem;
  }

  .ant-table-container,
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    border: none;
  }
  .ant-modal-body {
    padding-left: 3rem;
  }
`;

export const ContentOwnerDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const Country = styled.img`
  width: 1.5rem;
  height: 1.5rem;
`;

export const ActiveStatusIcon = styled(CheckCircleTwoTone)`
  font-size: 16px;
`;

export const InactiveStatusIcon = styled(CloseCircleTwoTone)`
  font-size: 16px;
`;
